import { PropsWithChildren } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { Link, To } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { ChapterLogoMobile } from "~/common/assets";
import SideBarItem from "~/common/components/ui/sideBar/SideBarItem";
import { Item } from "~/common/utils/types/sideBarTypes";
import { Avatar } from "../Avatar";

interface SidebarContentProps extends PropsWithChildren {
  menuItems: Item[];
  middleMenuItems?: Item[];
  bottomMenuItems?: Item[];
  handleItemChange: (item: Item) => void;
  onClose: () => void;
  homePath: To;
  user?: {
    image?: string;
    name?: string;
    organizationName?: string;
  };
}

export const SideBarContent = ({
  menuItems,
  middleMenuItems,
  bottomMenuItems,
  handleItemChange,
  onClose,
  homePath,
  children,
  user,
}: SidebarContentProps) => (
  <div className="fixed inset-0 z-30 flex w-28 flex-col overflow-auto border-r border-primary-200 bg-primary-50">
    <div className={twMerge("flex h-full flex-col justify-between")}>
      <div className="flex h-full flex-col gap-5">
        <div className="flex items-center justify-center pt-8">
          <Link to={homePath} onClick={onClose}>
            <img
              src={ChapterLogoMobile}
              alt="Chapter Logo"
              width={50}
              className="origin-center scale-150"
            />
          </Link>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div>
            {menuItems.map(
              (item) =>
                !item.disabled && (
                  <SideBarItem
                    onClick={() => handleItemChange(item)}
                    key={item.name}
                    selected={item.current}
                    Icon={item.Icon}
                  >
                    {item.name}
                  </SideBarItem>
                ),
            )}
            {middleMenuItems?.length && (
              <>
                <div
                  aria-hidden="true"
                  className="mx-2.5 my-2 border-t border-primary-200"
                />
                {middleMenuItems.map(
                  (item) =>
                    !item.disabled && (
                      <SideBarItem
                        onClick={item.onClick}
                        key={item.name}
                        className="group items-center"
                        Icon={item.Icon}
                      >
                        {item.name}
                      </SideBarItem>
                    ),
                )}
              </>
            )}
          </div>
          <div className="flex justify-center p-2 pb-5">
            {bottomMenuItems?.length &&
              (user ? (
                <Popover className="relative outline-none">
                  {({ open }) => {
                    return (
                      <>
                        <PopoverPanel
                          anchor="right end"
                          className="z-50 flex flex-col rounded-lg border border-neutral-60 bg-white py-2 shadow-sm [--anchor-gap:8px]"
                        >
                          <div className="mx-3.5 mb-2 flex items-center border-b pb-3 pt-1 transition-all duration-300 ease-in-out">
                            <Avatar
                              className="mr-2 size-8 rounded-full border border-white"
                              alt="profilePicture"
                              src={user.image}
                            />
                            <div>
                              <div className="text-sm font-semibold text-[#525D6A]">
                                {user.name}
                              </div>
                              <div className="text-xs text-[#525D6A]">
                                {user.organizationName}
                              </div>
                            </div>
                          </div>

                          {bottomMenuItems
                            .filter(({ disabled }) => !disabled)
                            .map((item) => (
                              <SideBarItem
                                inline
                                onClick={() => handleItemChange(item)}
                                key={item.name}
                                className="rounded-none px-5 py-2.5 font-medium"
                                selected={item.current}
                              >
                                <item.Icon className="size-5 stroke-2" />
                                {item.name}
                              </SideBarItem>
                            ))}
                        </PopoverPanel>
                        <PopoverButton
                          className={twMerge(
                            "rounded-full p-1 outline-none transition-all duration-300 ease-in-out hover:bg-primary-100",
                            open && "bg-primary-100",
                          )}
                        >
                          <Avatar
                            className="size-10 rounded-full border border-white"
                            alt="profilePicture"
                            src={user.image}
                          />
                        </PopoverButton>
                      </>
                    );
                  }}
                </Popover>
              ) : (
                <div className="flex flex-col">
                  {bottomMenuItems
                    .filter(({ disabled }) => !disabled)
                    .map((item) => (
                      <SideBarItem
                        onClick={() => handleItemChange(item)}
                        key={item.name}
                        selected={item.current}
                        Icon={item.Icon}
                      >
                        {item.name}
                      </SideBarItem>
                    ))}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
    {children}
  </div>
);
